import { Eyebrow } from '../brand';
import { HERO_SHOT, type Photo } from '../catalog';
import { useHref, useLang } from '../lang';
import { Pic } from '../pic';
import { demoHref, type Line } from '../routes';
import { Device } from './Software';

export function SectionHead({ id, prefix, tone = 'ink' }: { id: string; prefix: string; tone?: 'ink' | 'paper' }) {
  const { t } = useLang();
  return (
    <header className="split-head" data-reveal>
      <div>
        <Eyebrow k={`${prefix}.eyebrow`} tone={tone} />
        <h2 id={id} className="h2">
          {t(`${prefix}.title`)}
        </h2>
      </div>
      <p className="lead">{t(`${prefix}.lead`)}</p>
    </header>
  );
}

export function Subnav({ items }: { items: readonly { href: string; key: string }[] }) {
  const { t } = useLang();
  return (
    <nav className="subnav" aria-label={t('nav.page')}>
      <div className="frame subnav-bar">
        {items.map((item) => (
          <a key={item.href} href={item.href}>
            {t(item.key)}
          </a>
        ))}
      </div>
    </nav>
  );
}

/** Hero for a line page. Both lines use an app screenshot. */
export function LineHero({ line }: { line: 'waste' | 'vision' }) {
  const { t } = useLang();
  const href = useHref();
  const k = (s: string) => t(`${line}.hero.${s}`);
  return (
    <section className="hero line-hero" id="top" aria-labelledby="hero-title">
      <div className="hero-grid">
        <div className="hero-copy">
          <Eyebrow k={`${line}.hero.eyebrow`} />
          <h1 id="hero-title" className="display">
            {k('title')}
          </h1>
          <p className="hero-lead">{k('lead')}</p>
          <div className="actions">
            <a className="btn btn-primary" href={href(demoHref(line))}>
              {k('cta')}
            </a>
          </div>
          <p className="hero-note">{k('note')}</p>
        </div>
        <figure className="hero-media hero-media-app">
          <div className="stage stage-pair">
            <Device shot={HERO_SHOT[line].laptop} priority />
            <Device shot={HERO_SHOT[line].phone} priority />
          </div>
        </figure>
      </div>
    </section>
  );
}

export function Why({ prefix, ids, tone = 'paper' }: { prefix: string; ids: readonly number[]; tone?: 'paper' | 'soft' | 'ink' }) {
  const { t } = useLang();
  const hasDetail = Boolean(t(`${prefix}.1.d`) && t(`${prefix}.1.d`) !== `${prefix}.1.d`);
  return (
    <section className={`band${tone === 'soft' ? ' band-soft' : tone === 'ink' ? ' band-ink' : ''} why`} aria-labelledby={`${prefix}-title`}>
      <div className="frame">
        <SectionHead id={`${prefix}-title`} prefix={prefix} tone={tone === 'ink' ? 'paper' : 'ink'} />
        <ul className={`why-grid${hasDetail ? ' why-grid-copy' : ''}`} role="list">
          {ids.map((n) => (
            <li key={n} data-reveal>
              <h3 className="h4">{t(`${prefix}.${n}`)}</h3>
              {hasDetail && <p>{t(`${prefix}.${n}.d`)}</p>}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function HowYouStart({
  line,
  prefix,
  photo,
  specs,
}: {
  line: 'waste' | 'vision';
  prefix: string;
  photo: Photo;
  specs: readonly string[];
}) {
  const { t } = useLang();
  const href = useHref();
  return (
    <section className="band trial" id="start" aria-labelledby={`${prefix}-title`}>
      <div className="frame">
        <div className="offer-grid">
          <figure className="offer-media" data-reveal>
            <Pic photo={photo} alt={t(`${prefix}.alt`)} />
          </figure>
          <div className="offer-copy" data-reveal>
            <Eyebrow k={`${prefix}.eyebrow`} />
            <h2 id={`${prefix}-title`} className="h2">
              {t(`${prefix}.title`)}
            </h2>
            <p className="lead">{t(`${prefix}.lead`)}</p>
            <dl className="spec">
              {specs.map((k) => (
                <div key={k}>
                  <dt className="mono">{t(`${prefix}.k.${k}`)}</dt>
                  <dd>{t(`${prefix}.v.${k}`)}</dd>
                </div>
              ))}
            </dl>
            <div className="actions">
              <a className="btn btn-primary" href={href(demoHref(line))}>
                {t(`${line}.hero.cta`)}
              </a>
            </div>
          </div>
        </div>
        <div className="how">
          <h3 className="h3 how-title" data-reveal>
            {t(`${prefix}.how`)}
          </h3>
          <ol className="timeline">
            {(['s1', 's2', 's3'] as const).map((s) => (
              <li key={s} data-reveal>
                <p className="mono timeline-when">{t(`${prefix}.${s}.when`)}</p>
                <h4 className="timeline-title">{t(`${prefix}.${s}.title`)}</h4>
                <p>{t(`${prefix}.${s}.body`)}</p>
              </li>
            ))}
          </ol>
        </div>
        <div className="how how-roll">
          <h3 className="h3 how-title" data-reveal>
            {t(`${prefix}.roll`)}
          </h3>
          <ol className="steps steps-5">
            {[1, 2, 3, 4, 5].map((n) => (
              <li key={n} data-reveal>
                <span className="mono steps-n" aria-hidden="true">
                  {n}
                </span>
                <h4 className="h4">{t(`${prefix}.r${n}`)}</h4>
                <p>{t(`${prefix}.r${n}.d`)}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

export function Closer({ titleKey, line }: { titleKey: string; line?: Line }) {
  const { t } = useLang();
  const href = useHref();
  const leadKey = line === 'waste' ? 'close.waste.lead' : line === 'vision' ? 'close.vision.lead' : 'close.lead';
  return (
    <section className="band band-soft closer" aria-labelledby="closer-title">
      <div className="frame closer-grid" data-reveal>
        <div>
          <Eyebrow k="close.eyebrow" />
          <h2 id="closer-title" className="h2">
            {t(titleKey)}
          </h2>
        </div>
        <div className="closer-end">
          <p className="lead">{t(leadKey)}</p>
          <a className="btn btn-primary" href={href(demoHref(line === 'both' ? undefined : line))}>
            {t('close.cta')}
          </a>
        </div>
      </div>
    </section>
  );
}
